<template>
  <SettingsPageLayout>
    <div class="mx-auto w-11/12 max-w-3xl">
      <div class="flex items-start justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold leading-tight text-gray-900">Custom fields</h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Capture extra data on candidates and teammates and reference it in e-signature documents.
          </p>
        </div>
        <Button class="shrink-0 gap-2" @click="openNew"> <Plus class="h-4 w-4" /> Add field </Button>
      </div>

      <div class="mt-6">
        <HFetchQuery :query="customFieldsQuery">
          <template #default="{ data: customFields }">
            <div class="flex flex-col gap-2">
              <CustomFieldItem
                v-for="customField in customFields"
                :key="customField.id"
                :field="customField"
                @select="openEdit(customField)"
              />

              <ZeroState v-if="customFields.length === 0">
                <p>Your team does not have any custom fields yet.</p>
              </ZeroState>
            </div>
          </template>
        </HFetchQuery>
      </div>
    </div>

    <CustomFieldFormSheet v-model:open="sheetOpen" :field="selectedField" @saved="customFieldsQuery.reload()" />
  </SettingsPageLayout>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import { Plus } from 'lucide-vue-next';
import SettingsPageLayout from '@/components/layouts/SettingsPageLayout/index.vue';
import ZeroState from '@/components/ZeroState.vue';
import { Button } from '@/components/ui/button';
import { Api } from '@/api';
import type { CustomFieldEntity } from '@/api/entity_types/CustomField';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import HFetchQuery from '@/lib/hfetch/HFetchQuery.vue';
import CustomFieldItem from './CustomFieldItem.vue';
import CustomFieldFormSheet from './CustomFieldFormSheet.vue';

const sheetOpen = ref(false);
const selectedField = ref<CustomFieldEntity | null>(null);

const customFieldsQuery = useHFetchQuery(async () => {
  const response = await Api.CustomFields.listCustomFields();
  return { status: 'success', data: response?.records ?? [] };
});

const openNew = () => {
  selectedField.value = null;
  sheetOpen.value = true;
};

const openEdit = (customField: CustomFieldEntity) => {
  selectedField.value = customField;
  sheetOpen.value = true;
};
</script>
